import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import StaffActivityFilters from "./StaffActivityFilters";
import StaffActivityTable from "./StaffActivityTable";
import { loadDecrypted } from "Common Usage/localStorageUtils";

const BASE_URL = process.env.REACT_APP_API_URL;
const ITEMS_PER_PAGE = 5;

// ─── Normalize API log → shape StaffActivityTable expects ────────────────────
const normalizeLog = (log) => ({
  id: log._id,
  staffId: log.staff?._id ?? null, // used by table's row-click → staff detail modal
  staffName: log.staff?.fullName ?? "—",
  phoneNumber: log.staff?.phoneNumber ?? "",
  action: log.action ?? "—",
  description: log.description ?? "",
  timestamp: log.createdAt, // "YYYY-MM-DD HH:mm:ss" — Date() handles it
});

const StaffActivityPage = () => {
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [selectedStaff, setSelectedStaff] = useState(null); // full staff object | null
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination (client-side over the filtered list)
  const [currentPage, setCurrentPage] = useState(1);

  // Abort ref – cancel the previous request on each new fetch / unmount
  const abortRef = useRef(null);

  // ── Fetch logs ─────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async (staff = null) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const token = loadDecrypted("HeartConnect-Token");
      const headers = { ...(token && { Authorization: `Bearer ${token}` }) };

      const staffId = staff?._id ?? staff?.id ?? null;

      // Branch: specific staff vs all staff
      const url = staffId
        ? `${BASE_URL}/staffs-activity-log/activity-by-staff/${staffId}`
        : `${BASE_URL}/staffs-activity-log/all-staffs-activity-log`;

      const { data } = await axios.get(url, {
        headers,
        signal: abortRef.current.signal,
      });

      if (!data.success) {
        throw new Error(data.message || "Failed to fetch activity logs.");
      }

      setAllLogs((data.data ?? []).map(normalizeLog));
    } catch (err) {
      if (err.name === "CanceledError" || axios.isCancel?.(err)) return;
      console.error("[StaffActivityPage] fetchLogs:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Something went wrong. Please try again."
      );
      setAllLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch whenever selected staff changes (null = all)
  useEffect(() => {
    fetchLogs(selectedStaff);
    setCurrentPage(1);
  }, [fetchLogs, selectedStaff]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // ── Client-side search (instant, no extra request) ────────────────────────
  const filteredLogs = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return allLogs;
    return allLogs.filter(
      (log) =>
        log.staffName?.toLowerCase().includes(q) ||
        log.action?.toLowerCase().includes(q) ||
        log.description?.toLowerCase().includes(q) ||
        log.phoneNumber?.toLowerCase().includes(q)
    );
  }, [allLogs, searchTerm]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages = Math.max(
    1,
    Math.ceil(filteredLogs.length / ITEMS_PER_PAGE)
  );
  const safePage = Math.min(currentPage, totalPages);

  const paginatedLogs = filteredLogs.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleStaffChange = (staff) => {
    setSelectedStaff(staff); // null → "All staff"
    setSearchTerm(""); // clear search so results aren't stale
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pt-4">
      <StaffActivityFilters
        selectedStaff={selectedStaff}
        onStaffChange={handleStaffChange}
        searchTerm={searchTerm}
        onSearch={handleSearch}
        loading={loading}
      />

      {/* Error banner with retry */}
      {error && !loading && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={() => fetchLogs(selectedStaff)}
            className="ml-4 shrink-0 rounded-md bg-red-100 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      )}

      <StaffActivityTable
        activities={paginatedLogs}
        currentPage={safePage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        loading={loading}
      />
    </div>
  );
};

export default StaffActivityPage;
